import { useEffect, useState } from "react";
import { UpdateNotice } from "@/components/releases/UpdateNotice";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { FeaturePreviewGate } from "./FeaturePreviewGate";
import { useAuth } from "@/app/auth";
import { X } from "lucide-react";
import { BreadcrumbProvider } from "./breadcrumb";
import { Sidebar } from "./Sidebar";
import { Topbar } from "./Topbar";
import { CommandPalette } from "./CommandPalette";
import { EnvironmentBanner } from "./EnvironmentBanner";
import { cn } from "@/lib/cn";
import { useTranslation } from "react-i18next";

export function AppLayout() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (user?.portalSupplierId && !pathname.startsWith("/portal")) {
    return <Navigate to="/portal" replace />;
  }

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-paper">
      <EnvironmentBanner />
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* Desktop sidebar */}
        <aside className="hidden shrink-0 border-r border-line lg:block">
          <Sidebar collapsed={collapsed} />
        </aside>

        {/* Mobile drawer */}
        {mobileNav && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-ink/30" onClick={() => setMobileNav(false)} />
            <div className="absolute inset-y-0 left-0 flex">
              <div className="border-r border-line bg-paper shadow-[var(--shadow-pop)]">
                <Sidebar collapsed={false} onNavigate={() => setMobileNav(false)} />
              </div>
              <button
                type="button"
                onClick={() => setMobileNav(false)}
                className="m-2 grid size-9 self-start place-items-center rounded-sm bg-surface text-ink-soft shadow-[var(--shadow-card)]"
                aria-label={t("common.closeNavigation")}
              >
                <X className="size-5" />
              </button>
            </div>
          </div>
        )}

        <BreadcrumbProvider>
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            <Topbar
              onOpenSearch={() => setPaletteOpen(true)}
              onToggleCollapse={() => setCollapsed((c) => !c)}
              collapsed={collapsed}
              onOpenMobileNav={() => setMobileNav(true)}
            />
            <UpdateNotice />
            <main className={cn("min-h-0 flex-1 overflow-y-auto")}>
              <FeaturePreviewGate><Outlet /></FeaturePreviewGate>
            </main>
          </div>
        </BreadcrumbProvider>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
